import { agaveService } from 'app/services/agave.service';
import {
  ERPPostingStatus,
  HighnoteTransactionEvent,
  HighnoteTransactionStatus,
  AgaveAPInvoicesCreateExpenseInput,
  AgaveAPInvoicesLineItemInput,
} from 'API';
import { PostingContext, Subscription } from './types';
import { createPostingSubscription } from './sharedPostingUtils';
import { formatAmountForAgavePost, stripPaidolPrefix } from 'app/services/agave/erpUtils';
import { AgaveSourceSystem } from 'app/services/agave/agaveConstants';

/**
 * Builds Acumatica AP Invoice payload for queue-based posting.
 * Uses Agave POST /ap-invoices with type "Bill" for purchases or "Debit Adjustment" for refunds.
 *
 * Debit Adjustment is the correct Acumatica document type for refunds: it reduces the AP balance
 * owed to a vendor. The amount must be positive because the document type itself
 * implies the credit direction.
 *
 * Rules (per spike):
 * - type: "Bill" | "Debit Adjustment", vendor_id, line_items with at least one item.
 * - Each line: type: "Regular", quantity, unit_cost, ledger_account_id.
 * - Do NOT set tax_code_id or tax_amount on Regular line items (Agave/Acumatica reject them).
 */
export function buildAcumaticaAPInvoicePayload(
  transaction: HighnoteTransactionEvent,
  agaveAccountToken: string
): AgaveAPInvoicesCreateExpenseInput {
  const isRefund = transaction.status === HighnoteTransactionStatus.REFUNDED;
  const { amount, amountValue } = formatAmountForAgavePost(
    transaction.approvedAmount?.value ?? 0,
    transaction.status
  );
  const unitCost = isRefund ? Math.abs(amountValue).toFixed(2) : amount;
  const issueDate = new Date(transaction.createdAt).toISOString().split('T')[0];
  const description = (transaction.merchantName || '') + (transaction.note ? ' - ' + transaction.note : '');
  const number = transaction.id.substring(0, 15);
  const merchantName = transaction.merchantName || 'Merchant';

  const sourceData: Record<string, { value: string }> = {};
  if (transaction.agaveDepartment?.code) {
    sourceData.Branch = { value: transaction.agaveDepartment.code };
  }
  if (transaction.agaveSubaccount?.code) {
    sourceData.Subaccount = { value: transaction.agaveSubaccount.code };
  }

  const line_items: AgaveAPInvoicesLineItemInput[] = [
    {
      type: 'Regular',
      quantity: '1',
      unit_cost: unitCost,
      ledger_account_id: stripPaidolPrefix(transaction.agaveGlCodeId),
      description: description || undefined,
      ...(transaction.agaveProjectId && { project_id: stripPaidolPrefix(transaction.agaveProjectId) }),
      ...(stripPaidolPrefix(transaction.agaveSubJob?.agaveSubJobId || transaction.agaveSubJobId) && {
        sub_job_id: stripPaidolPrefix(transaction.agaveSubJob?.agaveSubJobId || transaction.agaveSubJobId),
      }),
      ...(transaction.agaveCostCodeId && { cost_code_id: stripPaidolPrefix(transaction.agaveCostCodeId) }),
      ...(transaction.agaveCostTypeId && { cost_type_id: stripPaidolPrefix(transaction.agaveCostTypeId) }),
      ...(Object.keys(sourceData).length > 0 && { source_data: sourceData as unknown as string }),
    },
  ];

  const payload: AgaveAPInvoicesCreateExpenseInput = {
    account_token: agaveAccountToken,
    type: isRefund ? 'Debit Adjustment' : 'Bill',
    erp: AgaveSourceSystem.ACUMATICA,
    vendor_id: stripPaidolPrefix(transaction.agaveVendorId),
    line_items,
    issue_date: issueDate,
    due_date: issueDate,
    description,
    number,
    merchantName,
    status: 'Open',
    ...(transaction.agaveProjectId && { project_id: stripPaidolPrefix(transaction.agaveProjectId) }),
  };

  return payload;
}

export interface AcumaticaPostingParams {
  transactionId: string;
  paidolId: string;
  transaction: HighnoteTransactionEvent;
  agaveAccountToken: string;
}

/**
 * Handles Acumatica AP Invoice (Bill/Debit Adjustment) posting via queue.
 */
export async function postAcumaticaTransaction(
  params: AcumaticaPostingParams,
  context: PostingContext
): Promise<void> {
  const { transactionId, paidolId, transaction: txn, agaveAccountToken } = params;
  const {
    dispatch,
    enqueueSnackbar,
    t,
    setIsAgaveLoading,
    setTransactionPosting,
    captureException,
    mapAgaveErrorMessage,
  } = context;

  setIsAgaveLoading(true);

  let subscription: Subscription | undefined;

  try {
    const postingData = buildAcumaticaAPInvoicePayload(txn, agaveAccountToken);
    const postingDataString = JSON.stringify(postingData);

    subscription = createPostingSubscription(transactionId, context);

    // Start the async Acumatica posting via the CDK v2-agave Lambda (SEE-308).
    // agaveService throws on transport / GraphQL errors, so we only need to
    // branch on the returned status.
    const result = await agaveService.startAcumaticaPosting({
      input: {
        transactionId,
        paidolId,
        erp: AgaveSourceSystem.ACUMATICA,
        postingType: 'AP_INVOICE',
        postingData: postingDataString,
      },
    });

    if (result?.status === ERPPostingStatus.PENDING) {
      enqueueSnackbar(t('submitExpense.postingExpense'), { variant: 'info' });
    } else {
      subscription.unsubscribe();
      const responseStatus = result?.status;
      const rawError = result?.error || 'Failed to start Acumatica posting';
      const errorMessage = mapAgaveErrorMessage(rawError, t);

      const { applyLocalTransactionUpdates } = context;
      dispatch(
        applyLocalTransactionUpdates([
          {
            id: transactionId,
            changes: {
              erpPostingStatus: responseStatus || null,
              erpPostingError: errorMessage,
            },
          },
        ]) as unknown as { type: string; payload: unknown }
      );

      throw new Error(errorMessage);
    }
  } catch (error) {
    subscription?.unsubscribe();
    setTransactionPosting({ id: transactionId, isPosting: false });
    setIsAgaveLoading(false);

    captureException(error);
    const rawMessage = (error as Error).message || t('submitExpense.error');
    const errorMessage = mapAgaveErrorMessage(rawMessage, t);
    enqueueSnackbar(errorMessage, { variant: 'error' });

    const { applyLocalTransactionUpdates } = context;
    dispatch(
      applyLocalTransactionUpdates([
        {
          id: transactionId,
          changes: {
            erpPostingStatus: null,
            erpPostingError: errorMessage,
          },
        },
      ]) as unknown as { type: string; payload: unknown }
    );
  }
}
